import { Button } from '@mui/material';
import { useAtomValue, useSetAtom } from 'jotai';
import { useMemo, useState } from 'react';
import { Link, useMatch } from 'react-router';
import { useTranslation } from 'react-i18next';
import { RoutePaths } from '../routes/AppRoutes';
import { commonSnackbarPropsAtom } from '../common/state/state';
import { useAddWishlistSupplierMutation } from '../mutations/__generated__/addWishlistSupplier.generated';
import { useRemoveWishlistSupplierMutation } from '../mutations/__generated__/removeWishlistSupplier.generated';
import { GetWishlistSuppliersDocument } from '../queries/__generated__/getWishlistSuppliers.generated';
import { currentContextAtom } from '../state';
import { storeWishlistActivity } from './wishlist.utils';
import {
  wishlistSupplierAtom,
} from './state';

export interface UseWishlistSupplierResult {
  isInWishlist: boolean;
  isLoading: boolean;
  toggleFavorite: () => Promise<void>;
}

export default function useWishlistSupplier(
  supplierId: string,
): UseWishlistSupplierResult {
  const matchWishlistPage = useMatch(RoutePaths.WISHLIST);
  const [addWishlistSupplier] = useAddWishlistSupplierMutation({
    refetchQueries: [GetWishlistSuppliersDocument],
    awaitRefetchQueries: true,
  });
  const [removeWishlistSupplier] = useRemoveWishlistSupplierMutation({
    refetchQueries: [GetWishlistSuppliersDocument],
    awaitRefetchQueries: true,
  });
  const { t } = useTranslation(['wishlist']);
  const { roleAssignmentId, companyId } = useAtomValue(currentContextAtom);
  const wishlistSuppliers = useAtomValue(wishlistSupplierAtom);
  const setCommonSnackbarProps = useSetAtom(commonSnackbarPropsAtom);
  const [isLoading, setIsLoading] = useState(false);

  const isInWishlist = wishlistSuppliers.some(
    (item) => item.supplier_id === supplierId,
  );

  const snackbarAction = useMemo((): React.JSX.Element => {
    if (matchWishlistPage) {
      // eslint-disable-next-line react/jsx-no-useless-fragment
      return <></>;
    }

    return (
      <Button disableRipple variant="text" component={Link} to={RoutePaths.WISHLIST}>
        {t('common:see')}
      </Button>
    )
  }, [matchWishlistPage]);

  const toggleFavorite = async (): Promise<void> => {
    const variables = {
      companyId: companyId!,
      role_assignment_id: roleAssignmentId!,
      supplier_id: supplierId,
    };

    setIsLoading(true);

    if (isInWishlist) {
      const { errors } = await removeWishlistSupplier({ variables });

      if (errors) {
        setCommonSnackbarProps({
          label: t('wishlist:error_when_updating_wishlist'),
          snackbarProps: {
            open: true,
          },
          alertProps: {
            severity: 'error',
          },
        });
      } else {
        storeWishlistActivity(roleAssignmentId!);
        setCommonSnackbarProps({
          label: t('wishlist:supplier_removed_from_wishlist'),
          snackbarProps: {
            open: true,
          },
          alertProps: {
            action: snackbarAction,
          },
        });
      }
    } else {
      const { errors } = await addWishlistSupplier({ variables });

      if (errors) {
        setCommonSnackbarProps({
          label: t('wishlist:error_when_updating_wishlist'),
          snackbarProps: {
            open: true,
          },
          alertProps: {
            severity: 'error',
          },
        });
      } else {
        storeWishlistActivity(roleAssignmentId!);
        setCommonSnackbarProps({
          label: t('wishlist:supplier_added_to_wishlist'),
          snackbarProps: {
            open: true,
          },
          alertProps: {
            action: snackbarAction,
          },
        });
      }
    }

    setIsLoading(false);
  };

  return {
    isInWishlist,
    isLoading,
    toggleFavorite,
  };
}
